/**
 * iCal nesting remnants (category N).
 *
 * Airbnb calendar sync can re-emit shrinking nested UIDs (same property, shared
 * checkout / containment, same UID prefix) with no Export twin. These stay blue
 * on the calendar and are not synthetic A/B/C ghosts.
 *
 * See docs/ICAL_NESTING_REMNANTS_PLAN.md
 */

import { isManualOrExportSource } from './bookingSources';

export type NestingRemnantClassifiableBooking = {
  id: number;
  source?: string | null;
  property_id?: number | null;
  check_in_date?: string | null;
  check_out_date?: string | null;
  external_booking_id?: string | null;
  ical_uid?: string | null;
  reservation_url?: string | null;
  guest_name?: string | null;
  status?: string | null;
  is_merged_pair?: boolean;
};

function ymd(value?: string | null): string {
  if (!value) return '';
  const s = String(value);
  return /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : s;
}

function parseYmd(value?: string | null): { t: number; ymd: string } | null {
  const s = ymd(value);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  const [y, m, d] = s.split('-').map(Number);
  return { t: Date.UTC(y, m - 1, d), ymd: s };
}

/** Stem before first `-` for `hex-rest@airbnb.com` UIDs; otherwise trimmed local id. */
export function airbnbIcalUidPrefix(booking: {
  ical_uid?: string | null;
  external_booking_id?: string | null;
}): string {
  const raw = String(booking.ical_uid || booking.external_booking_id || '')
    .trim()
    .toLowerCase();
  if (!raw) return '';
  const local = raw.includes('@') ? raw.split('@')[0] : raw;
  const dash = local.indexOf('-');
  if (dash > 0) return local.slice(0, dash);
  return local;
}

function isIcalSource(booking: NestingRemnantClassifiableBooking): boolean {
  return String(booking.source || '').trim() === 'iCal' && !booking.is_merged_pair;
}

function isExportOrManual(booking: NestingRemnantClassifiableBooking): boolean {
  if (booking.is_merged_pair || String(booking.source || '').trim() === 'Merged') return true;
  const s = String(booking.source || '').trim();
  return isManualOrExportSource(s);
}

const SYNTHETIC_EXT_RE = /^airbnb_[A-Za-z0-9]+_\d{4}-\d{2}-\d{2}_\d{4}-\d{2}-\d{2}$/i;

function isSyntheticExt(booking: NestingRemnantClassifiableBooking): boolean {
  return SYNTHETIC_EXT_RE.test(String(booking.external_booking_id || '').trim());
}

/** Real Airbnb calendar UID style (`…@airbnb.com`), not export HM codes / synthetics. */
export function isRealAirbnbIcalUid(booking: NestingRemnantClassifiableBooking): boolean {
  const uid = String(booking.ical_uid || '').trim();
  const ext = String(booking.external_booking_id || '').trim();
  const blob = `${uid}\n${ext}`.toLowerCase();
  return blob.includes('@airbnb.com');
}

function hasReservationUrl(booking: NestingRemnantClassifiableBooking): boolean {
  return Boolean(String(booking.reservation_url || '').trim());
}

/** Confirmation-style export id (e.g. HMxxxxxxxx), not an email UID. */
function looksLikeConfirmationCode(booking: NestingRemnantClassifiableBooking): boolean {
  const ext = String(booking.external_booking_id || '')
    .trim()
    .toUpperCase();
  if (!ext || ext.includes('@')) return false;
  return /^[A-Z0-9]{8,14}$/.test(ext);
}

function rangesOverlap(
  aIn: number,
  aOut: number,
  bIn: number,
  bOut: number
): boolean {
  return aIn < bOut && bIn < aOut;
}

/** A strictly contains B as [checkIn, checkOut) stays. */
function rangeContains(
  outerIn: number,
  outerOut: number,
  innerIn: number,
  innerOut: number
): boolean {
  return outerIn <= innerIn && outerOut >= innerOut && (outerIn < innerIn || outerOut > innerOut);
}

type DatedIcal = NestingRemnantClassifiableBooking & {
  inT: number;
  outT: number;
  prefix: string;
};

function asDatedIcal(b: NestingRemnantClassifiableBooking): DatedIcal | null {
  if (!isIcalSource(b)) return null;
  if (isSyntheticExt(b)) return null;
  if (!isRealAirbnbIcalUid(b)) return null;
  if (hasReservationUrl(b)) return null;
  if (looksLikeConfirmationCode(b)) return null;
  const ci = parseYmd(b.check_in_date);
  const co = parseYmd(b.check_out_date);
  if (!ci || !co || co.t <= ci.t) return null;
  const prefix = airbnbIcalUidPrefix(b);
  if (!prefix || prefix.length < 6) return null;
  return { ...b, inT: ci.t, outT: co.t, prefix };
}

function exportOverlapsAny(
  members: DatedIcal[],
  all: NestingRemnantClassifiableBooking[]
): boolean {
  const propId = members[0]?.property_id;
  if (propId == null) return false;
  let minIn = members[0].inT;
  let maxOut = members[0].outT;
  for (let i = 1; i < members.length; i += 1) {
    minIn = Math.min(minIn, members[i].inT);
    maxOut = Math.max(maxOut, members[i].outT);
  }
  for (let i = 0; i < all.length; i += 1) {
    const o = all[i];
    if (o.property_id !== propId) continue;
    if (!isExportOrManual(o)) continue;
    const oi = parseYmd(o.check_in_date);
    const oo = parseYmd(o.check_out_date);
    if (!oi || !oo) continue;
    if (rangesOverlap(minIn, maxOut, oi.t, oo.t)) return true;
  }
  return false;
}

/**
 * Two iCals nest if same property, overlapping, and one range strictly contains
 * the other (includes shared-checkout shrinking check-in chains).
 */
export function icalsNest(a: DatedIcal, b: DatedIcal): boolean {
  if (a.property_id !== b.property_id) return false;
  if (a.id === b.id) return false;
  if (!rangesOverlap(a.inT, a.outT, b.inT, b.outT)) return false;
  return (
    rangeContains(a.inT, a.outT, b.inT, b.outT) || rangeContains(b.inT, b.outT, a.inT, a.outT)
  );
}

/**
 * Map booking id → true for every iCal nesting remnant (category N) in the set.
 * Entire chain is flagged when members share UID prefix, nest, and have no Export overlap.
 */
export function classifyIcalNestingRemnants(
  bookings: NestingRemnantClassifiableBooking[]
): Map<number, true> {
  const dated: DatedIcal[] = [];
  for (let i = 0; i < bookings.length; i += 1) {
    const d = asDatedIcal(bookings[i]);
    if (d) dated.push(d);
  }

  const out = new Map<number, true>();
  if (dated.length < 2) return out;

  // Union-find on nesting edges within same property + prefix
  const parent = new Map<number, number>();
  const find = (id: number): number => {
    let p = parent.get(id) ?? id;
    while (p !== (parent.get(p) ?? p)) {
      p = parent.get(p) ?? p;
    }
    parent.set(id, p);
    return p;
  };
  const union = (a: number, b: number) => {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent.set(ra, rb);
  };

  dated.forEach((d) => parent.set(d.id, d.id));

  for (let i = 0; i < dated.length; i += 1) {
    for (let j = i + 1; j < dated.length; j += 1) {
      const a = dated[i];
      const b = dated[j];
      if (a.property_id !== b.property_id) continue;
      if (a.prefix !== b.prefix) continue;
      if (icalsNest(a, b)) union(a.id, b.id);
    }
  }

  const byRoot = new Map<number, DatedIcal[]>();
  dated.forEach((d) => {
    const root = find(d.id);
    const list = byRoot.get(root) || [];
    list.push(d);
    byRoot.set(root, list);
  });

  byRoot.forEach((members) => {
    if (members.length < 2) return;
    if (exportOverlapsAny(members, bookings)) return;
    members.forEach((m) => out.set(m.id, true));
  });

  return out;
}

export function isIcalNestingRemnant(
  bookingId: number,
  remnantIds: Map<number, true> | Set<number>
): boolean {
  if (remnantIds instanceof Map) return remnantIds.has(bookingId);
  return remnantIds.has(bookingId);
}

/** Soft row background for nesting remnants in the table. */
export const ICAL_NESTING_REMNANT_ROW_SX = {
  bgcolor: 'rgba(158, 158, 158, 0.18)',
  '&:hover': { bgcolor: 'rgba(158, 158, 158, 0.30)' },
};

export const ICAL_NESTING_REMNANT_LEGEND = {
  label: 'N — iCal nesting remnant (no Export twin)',
  swatch: 'rgba(158, 158, 158, 0.45)',
};
